/**
 * Visual identity helpers for workspace switcher tiles.
 *
 * The sidebar tile (and any future "workspace chip" surface) needs:
 *   1. A single letter glyph for the workspace name.
 *   2. A deterministic background color so two workspaces sharing a
 *      first letter (Marketing + Manufacturing) are visually
 *      disambiguated at a glance — same workspace always gets the
 *      same color across sessions and devices.
 *
 * Pattern: Slack workspace switcher. The color is hashed off the
 * workspace `_id` (never the name) so a rename leaves the color
 * stable — only the letter glyph changes. No DB write, no migration.
 */

export interface WorkspaceTile {
  /** Tailwind gradient classes for the tile background — keep both
   * stops on one line so callers can drop into `className`. */
  gradient: string
  /** Tailwind border color to pair with the gradient (matches the
   * brighter stop so the tile has a subtle 1px highlight). */
  border: string
}

/** 12 hand-tuned tile palettes, legible against the dark sidebar
 * background. Order is fixed — the hash modulo this length picks the
 * tile, so adding/removing entries reshuffles every existing
 * workspace's color. Don't reorder without a reason. */
export const WORKSPACE_TILE_PALETTE: readonly WorkspaceTile[] = [
  { gradient: 'bg-gradient-to-br from-blue-600 to-blue-500',       border: 'border-blue-500' },
  { gradient: 'bg-gradient-to-br from-cyan-700 to-cyan-500',       border: 'border-cyan-500' },
  { gradient: 'bg-gradient-to-br from-emerald-700 to-emerald-500', border: 'border-emerald-500' },
  { gradient: 'bg-gradient-to-br from-lime-600 to-lime-500',       border: 'border-lime-500' },
  { gradient: 'bg-gradient-to-br from-amber-600 to-amber-500',     border: 'border-amber-500' },
  { gradient: 'bg-gradient-to-br from-orange-700 to-orange-500',   border: 'border-orange-500' },
  { gradient: 'bg-gradient-to-br from-rose-700 to-rose-500',       border: 'border-rose-500' },
  { gradient: 'bg-gradient-to-br from-fuchsia-700 to-fuchsia-500', border: 'border-fuchsia-500' },
  { gradient: 'bg-gradient-to-br from-violet-700 to-violet-500',   border: 'border-violet-500' },
  { gradient: 'bg-gradient-to-br from-indigo-700 to-indigo-500',   border: 'border-indigo-500' },
  { gradient: 'bg-gradient-to-br from-slate-600 to-slate-500',     border: 'border-slate-500' },
  { gradient: 'bg-gradient-to-br from-stone-600 to-stone-500',     border: 'border-stone-500' },
]

/** Extract the single uppercase letter glyph from a workspace name.
 *
 * Rules:
 *   - First **alphanumeric** character of the name (skip leading
 *     emoji, punctuation, whitespace — so `"🚀 Marketing"` → `"M"`,
 *     `"  Sales"` → `"S"`).
 *   - Uppercased so the tile always renders consistently.
 *   - Empty name or pure-symbol name falls back to `"?"` so the tile
 *     never collapses to an empty box.
 *
 * Single character only — multi-character glyphs would break the
 * icon-stacked sidebar rhythm (the tile is sized for one letter). */
export function extractInitial(name: string | null | undefined): string {
  if (!name) return '?'
  for (const ch of name) {
    if (/^[a-zA-Z0-9]$/.test(ch)) return ch.toUpperCase()
  }
  return '?'
}

/** FNV-1a 32-bit hash over the input string. Used to map a workspace
 * id deterministically onto a palette index. Tiny, no dependencies,
 * stable across JS engines (which `string.hashCode`-style helpers
 * are not — `Math.imul` is the only quirk worth knowing about, and
 * it's been baseline for years).
 *
 * Returns an unsigned 32-bit integer. */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** Pick a palette tile for the given workspace id. Same id always
 * returns the same tile across sessions and devices — that's the
 * whole point of the hash. The id is the stable input (never the
 * name — that'd change on rename and the tile color would shift,
 * which would surprise the user). */
export function getWorkspaceTile(workspaceId: string): WorkspaceTile {
  const hash = fnv1a(workspaceId)
  return WORKSPACE_TILE_PALETTE[hash % WORKSPACE_TILE_PALETTE.length]!
}
